import { IQuote } from '@/interfaces';
import { toCurrency } from '@/utils/convert';
import { capitalize } from 'lodash-es';

/* 
  Discount orders from Sitecore 8:
  Motor:
  - Loyalty discount
  - Driver excellence bonus (no longer available)
  - No claim discount
  - Multi policy discount
  - Combined policy discount (no longer available)
  - Car alarm discount (no longer available)
  - Online discount (not shown on purchase form review summary)

  Home:
  - Online discount (shown last on purchase form review summary)
  - Loyalty discount
  - No claim discount (no longer available)
  - Household excellence bonus (no longer available)
  - Multi policy discount
  - Combined policy discount
  - Apartment discount (no longer available)
  - Home alarm discount (no longer available)
  - Senior discount (with senior card) (no longer available)
  - Over 55 discount (no senior card) (no longer available)

  Pet:
  - Online discount (shown last on purchase form review summary)
  - Loyalty discount
  - Multi policy discount
  - Combined policy discount (no longer available)
  - Multi Pet discount (no longer available)
*/
export function getDiscountsList(
  translation: any,
  quote: Partial<IQuote>,
  options: {
    showAsMonthly: boolean;
    showOnlineDiscount: boolean;
    showOnlineDiscountLast: boolean;
  }
) {
  const dictionary: Record<string, string> = translation.getLocaleMessage('en');

  const discounts: string[] = [];

  const onlineDiscount = options.showOnlineDiscount ? getOnlineDiscount(translation, quote, options) : null;
  if (onlineDiscount && !options.showOnlineDiscountLast) {
    discounts.push(onlineDiscount);
  }

  if (quote?.discounts?.eligibleForLoyaltyDiscount?.value) {
    discounts.push(
      dictionary.IQ_MemberDiscount.replace('#type#', capitalize(quote?.discounts?.highestMembershipLoyaltyLevel?.value))
    );
  }

  checkNoClaimDiscount(quote, discounts, dictionary);

  if (quote?.discounts?.eligibleForMultiPolicyDiscount?.value) {
    discounts.push(dictionary.IQ_MultiPolicyDiscount);
  }

  if (quote?.discounts?.eligibleForCombinedPolicyDiscount?.value) {
    discounts.push(dictionary.IQ_CombinePolicyDiscount);
  }

  if (quote?.discounts?.eligibleForSeniorCardDiscount?.value) {
    discounts.push(dictionary.IQ_SeniorDiscountWithSeniorCard);
  }

  if (quote?.discounts?.eligibleForSeniorDiscount?.value) {
    discounts.push(dictionary.IQ_Over55DiscountNoSeniorCard);
  }

  if (quote?.homeAndContentsQuote?.discounts?.eligibleForHouseHoldExcelenceBonus?.value) {
    discounts.push(dictionary.IQ_HouseholdExcellenceBonus);
  }

  if (quote?.homeAndContentsQuote?.contentsQuote?.discounts?.eligibleForApartmentDiscount?.value) {
    discounts.push(dictionary.IQ_ApartmentDiscount);
  }

  if (quote?.discounts?.eligibleForStaffDiscount?.value) {
    discounts.push(dictionary.IQ_StaffDiscount);
  }

  if (onlineDiscount && options.showOnlineDiscountLast) {
    discounts.push(onlineDiscount);
  }

  return discounts;
}

function checkNoClaimDiscount(quote: Partial<IQuote>, discounts: string[], dictionary: Record<string, string>) {
  if (
    quote?.homeAndContentsQuote?.homeQuote?.discounts?.eligibleForNoClaimDiscount?.value ||
    quote?.homeAndContentsQuote?.contentsQuote?.discounts?.eligibleForNoClaimDiscount?.value ||
    quote?.motorQuote?.primaryDriverDetails?.discounts?.eligibleForNoClaimDiscount?.value
  ) {
    discounts.push(dictionary.IQ_NoClaimDiscount);
  }
}

export function getOnlineDiscount(
  translation: any,
  quote: Partial<IQuote>,
  options = {
    showAsMonthly: false,
  }
) {
  const dictionary: Record<string, string> = translation.getLocaleMessage('en');

  if (quote.discounts?.eligibleForMonthlyOnlineDiscount?.value && options?.showAsMonthly) {
    return dictionary.IQ_OnlineDiscount.replace(
      '$#value#',
      toCurrency(quote?.discounts.totalMonthlyOnlineDiscountAmount?.value, false)
    );
  }

  if (quote?.discounts?.eligibleForYearlyOnlineDiscount?.value && !options?.showAsMonthly) {
    return dictionary.IQ_OnlineDiscount.replace(
      '$#value#',
      toCurrency(quote?.discounts.totalYearlyOnlineDiscountAmount?.value, false)
    );
  }

  return null;
}
